import { clsx, type ClassValue } from "clsx"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import {
  isValidPhoneNumber as LibPhoneIsValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js"
import { twMerge } from "tailwind-merge"

dayjs.extend(utc)

const DATE_TIME_FORMAT = "MMM DD YYYY hh:mm a"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a phone number into a readable international form
 * (e.g. "+19187741106" → "+1 918 774 1106"). Returns the original string
 * unchanged when it can't be parsed, and "" for empty input.
 */
export function formatPhoneNumber(phone?: string | null) {
  if (!phone) return ""
  const parsed = parsePhoneNumberFromString(phone)
  return parsed ? parsed.formatInternational() : phone
}

/**
 * Formats a UTC timestamp in the viewer's local (staff) timezone. Parses the
 * input as UTC — so it's correct whether or not the string carries a `Z`/offset
 * — then converts to local before formatting.
 */
export function formatDateTimeLocal(
  date?: string | number | Date | null
): string {
  if (!date) return ""
  return dayjs.utc(date).local().format(DATE_TIME_FORMAT)
}

/**
 * Triggers a browser download of a Blob. Appends the anchor to the DOM and
 * defers `revokeObjectURL` so the download isn't aborted in Firefox/Safari,
 * which can cancel an in-flight download when the URL is revoked synchronously.
 */
export function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.style.display = "none"
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}

export function formatDate(
  date?: string | number | Date | null | dayjs.Dayjs
): string {
  return dayjs(date).format("MMM DD, YYYY")
}

export const formatDateForAPI = (date?: Date) => {
  if (!date) return ""
  return dayjs(date).format("YYYY-MM-DD")
}

export function formatDateTime(
  date?: string | number | Date | null | dayjs.Dayjs
): string {
  return dayjs(date).format(DATE_TIME_FORMAT)
}

export function getRelativeTime(
  date?: string | number | Date | null | dayjs.Dayjs
): string {
  if (!date) return ""
  const now = dayjs()
  const target = dayjs(date)
  const diffInDays = now.diff(target, "day")
  const diffInHours = now.diff(target, "hour")
  const diffInMinutes = now.diff(target, "minute")

  if (diffInMinutes < 1) return "Just now"
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInHours < 24) return `${diffInHours}h ago`
  if (diffInDays === 0) return "Today"
  if (diffInDays === 1) return "Yesterday"
  if (diffInDays < 7) return `${diffInDays}d ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`
  return `${Math.floor(diffInDays / 365)}y ago`
}

/**
 * Formats a date as a relative time string (Today, Yesterday, X days ago, etc.)
 * Falls back to formatted date for dates older than 30 days
 */
export function formatRelativeDate(
  dateString?: string | number | Date | null
): string {
  if (!dateString) return "—"
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export function utcToZonedTime(date?: string | number | Date | null) {
  return dayjs(date).utc(true)
}

export function zonedTimeToUtc(
  date?: string | number | Date | null | dayjs.Dayjs
) {
  const dateTime = dayjs(date).utc(false)
  return dateTime
}

export const isValidPhoneNumber = (input: string) => {
  return LibPhoneIsValidPhoneNumber(input)
}

export const formatAddress = (param?: {
  zipCode?: string | null
  address?: string | null
  state?: string | null
}) => {
  if (!param?.address) return "-"
  const { address, state, zipCode } = param
  return `${address}, ${state}, ${zipCode}`
}
